import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../components/ui/Toast";
import {
  Button,
  Textarea,
  Card,
  Badge,
  Modal,
  FileUpload,
} from "../components/ui";

const STATO = {
  APERTA: 100000000,
  IN_CORSO: 100000001,
  COMPLETATA: 100000002,
  CONCLUSA: 100000004,
};

const STATO_CONFIG = {
  [STATO.APERTA]: { label: "Aperta", variant: "danger", color: "red" },
  [STATO.IN_CORSO]: { label: "In corso", variant: "warning", color: "yellow" },
  [STATO.COMPLETATA]: {
    label: "Completata",
    variant: "success",
    color: "green",
  },
  100000003: { label: "Verificata", variant: "info", color: "blue" }, // legacy
  [STATO.CONCLUSA]: { label: "Conclusa", variant: "default", color: "gray" },
};

const PRIORITA_CONFIG = {
  100000000: { label: "Bassa", variant: "default" },
  100000001: { label: "Media", variant: "warning" },
  100000002: { label: "Alta", variant: "danger" },
  100000003: { label: "Critica", variant: "danger" },
};

const TRANSIZIONI = {
  [STATO.APERTA]: [
    { stato: STATO.IN_CORSO, label: "Prendi in carico", variant: "primary" },
  ],
  [STATO.IN_CORSO]: [
    {
      stato: STATO.COMPLETATA,
      label: "Segna come completata",
      variant: "success",
    },
  ],
  [STATO.COMPLETATA]: [
    {
      stato: STATO.CONCLUSA,
      label: "Verifica esito",
      variant: "primary",
      action: "azioni_correttive.verifica",
    },
  ],
  [STATO.CONCLUSA]: [
    { stato: STATO.APERTA, label: "Riapri", variant: "outline" },
  ],
};

const COLORI_STATO = {
  red: { active: "#ef4444", past: "#fca5a5" },
  yellow: { active: "#eab308", past: "#fde047" },
  green: { active: "#22c55e", past: "#86efac" },
  blue: { active: "#3b82f6", past: "#93c5fd" },
  gray: { active: "#6b7280", past: "#d1d5db" },
};

function DettaglioAzioneCorrettivaPage() {
  const { id } = useParams();
  const { user, apiRequest, canPerformAction } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [azione, setAzione] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEsitoModal, setShowEsitoModal] = useState(false);
  const [esitoText, setEsitoText] = useState("");
  const [pendingStato, setPendingStato] = useState(null);

  // Annotazioni
  const [annotazioni, setAnnotazioni] = useState([]);
  const [nuovaAnnotazione, setNuovaAnnotazione] = useState("");
  const [savingNota, setSavingNota] = useState(false);

  // Allegati
  const [allegati, setAllegati] = useState([]);
  const [uploading, setUploading] = useState(false);

  // Annotazione durante cambio stato
  const [annotazioneStato, setAnnotazioneStato] = useState("");
  const [esitoValutazione, setEsitoValutazione] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isAdmin = user?.ruolo === 100000000;
  const canGestisci = canPerformAction("azioni_correttive.gestisci");
  const canVerifica = canPerformAction("azioni_correttive.verifica");
  const isFornitore = user?.ruolo === 100000005;

  // Puo annotare: chi ha gestisci, oppure fornitore assegnato
  const canAnnotateAction =
    isAdmin ||
    canGestisci ||
    (isFornitore && azione?.fornitore?.id === user?.fornitoreId);

  // Non mostrare form annotazione se azione conclusa
  const isOpen = azione && azione.stato !== STATO.CONCLUSA;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [data, allegatiRes] = await Promise.all([
          apiRequest(`/azioni-correttive/${id}`),
          apiRequest(`/azioni-correttive/${id}/allegati`).catch(() => ({
            value: [],
          })),
        ]);
        setAzione(data);
        setAnnotazioni(data.annotazioni || []);
        setAllegati(allegatiRes.value || []);
      } catch (error) {
        console.error("Error fetching azione correttiva:", error);
        showToast("Errore nel caricamento", "error");
        navigate("/azioni-correttive");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [id, apiRequest, showToast, navigate]);

  const handleCambioStato = async (nuovoStato) => {
    // Tutti i cambi stato passano dalla modale per permettere annotazioni
    setPendingStato(nuovoStato);
    setShowEsitoModal(true);
  };

  const handleConfermaStato = async () => {
    // Per chiusura/verifica serve esito valutazione
    const needsEsito = pendingStato === STATO.CONCLUSA;
    if (needsEsito && !esitoValutazione) {
      showToast(
        "Seleziona l'esito della valutazione (Positivo/Negativo)",
        "warning",
      );
      return;
    }

    setSaving(true);
    try {
      const payload = { nuovoStato: pendingStato };
      if (esitoText.trim()) payload.esito = esitoText;
      if (annotazioneStato.trim()) payload.annotazione = annotazioneStato;
      if (esitoValutazione)
        payload.esitoValutazione = parseInt(esitoValutazione);

      let result;
      if (pendingStato === STATO.CONCLUSA) {
        // Verifica esito: Positivo → Conclusa, Negativo → Riaperta automaticamente
        result = await apiRequest(`/azioni-correttive/${id}/chiudi`, {
          method: "PATCH",
          body: JSON.stringify({
            esitoValutazione: parseInt(esitoValutazione),
            esito: esitoText || null,
            annotazione: annotazioneStato || null,
          }),
        });
      } else {
        result = await apiRequest(`/azioni-correttive/${id}/stato`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      }

      // Reload data
      const [data, allegatiRes] = await Promise.all([
        apiRequest(`/azioni-correttive/${id}`),
        apiRequest(`/azioni-correttive/${id}/allegati`).catch(() => ({
          value: [],
        })),
      ]);
      setAzione(data);
      setAnnotazioni(data.annotazioni || []);
      setAllegati(allegatiRes.value || []);

      setShowEsitoModal(false);
      setEsitoText("");
      setAnnotazioneStato("");
      setEsitoValutazione("");
      setPendingStato(null);

      // Toast basato sull'esito reale (Positivo=Conclusa, Negativo=Riaperta)
      if (result?.esitoPositivo === false) {
        showToast(
          "Esito negativo - azione riaperta per ulteriori interventi",
          "warning",
        );
      } else if (result?.esitoPositivo === true) {
        showToast("Azione conclusa con esito positivo", "success");
      } else {
        showToast(
          `Stato aggiornato a "${STATO_CONFIG[data.stato]?.label || "?"}"`,
          "success",
        );
      }
    } catch (error) {
      showToast(error.message || "Errore", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleAggiungiAnnotazione = async () => {
    if (!nuovaAnnotazione.trim()) return;
    setSavingNota(true);
    try {
      await apiRequest(`/azioni-correttive/${id}/annotazioni`, {
        method: "POST",
        body: JSON.stringify({ testo: nuovaAnnotazione }),
      });

      // Reload annotazioni
      const data = await apiRequest(`/azioni-correttive/${id}`);
      setAnnotazioni(data.annotazioni || []);
      setNuovaAnnotazione("");
      showToast("Annotazione aggiunta", "success");
    } catch (error) {
      showToast(error.message || "Errore", "error");
    } finally {
      setSavingNota(false);
    }
  };

  const handleUploadAllegato = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("nome", `Allegato - ${file.name}`);

      await apiRequest(`/azioni-correttive/${id}/allegati`, {
        method: "POST",
        body: formData,
        headers: {},
      });

      // Reload
      const [allegatiRes, data] = await Promise.all([
        apiRequest(`/azioni-correttive/${id}/allegati`),
        apiRequest(`/azioni-correttive/${id}`),
      ]);
      setAllegati(allegatiRes.value || []);
      setAnnotazioni(data.annotazioni || []);
      showToast("Allegato caricato", "success");
    } catch (error) {
      showToast(error.message || "Errore nel caricamento", "error");
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await apiRequest(`/azioni-correttive/${id}`, { method: "DELETE" });
      showToast("Azione correttiva eliminata", "success");
      navigate("/azioni-correttive");
    } catch (error) {
      showToast(error.message || "Errore nell'eliminazione", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  if (!azione) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Azione correttiva non trovata</p>
        <Button
          variant="ghost"
          onClick={() => navigate("/azioni-correttive")}
          className="mt-4"
        >
          Torna alla lista
        </Button>
      </div>
    );
  }

  const statoConf = STATO_CONFIG[azione.stato] || STATO_CONFIG[STATO.APERTA];
  const prioConf =
    PRIORITA_CONFIG[azione.priorita] || PRIORITA_CONFIG[100000001];
  const transizioni = TRANSIZIONI[azione.stato] || [];

  const transizioniVisibili = transizioni.filter((t) => {
    if (t.action === "azioni_correttive.verifica") return canVerifica;
    if (isFornitore && t.stato === STATO.COMPLETATA) return true;
    return canGestisci;
  });

  // Label dinamico per modal
  const modalLabels = {
    [STATO.IN_CORSO]: {
      title: "Prendi in carico",
      desc: "Stai prendendo in carico questa azione correttiva. Puoi aggiungere una nota.",
      showEsitoValutazione: false,
    },
    [STATO.COMPLETATA]: {
      title: "Segna come completata",
      desc: "Descrivi cosa e stato fatto per risolvere il problema.",
      showEsitoValutazione: false,
    },
    [STATO.CONCLUSA]: {
      title: "Verifica esito azione correttiva",
      desc: "Seleziona l'esito della valutazione. Se Positivo l'azione verra conclusa, se Negativo verra riaperta per ulteriori interventi.",
      showEsitoValutazione: true,
    },
    [STATO.APERTA]: {
      title: "Riapri azione correttiva",
      desc: "Motiva la riapertura dell'azione.",
      showEsitoValutazione: false,
    },
  };

  const modalConf = modalLabels[pendingStato] || modalLabels[STATO.IN_CORSO];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Breadcrumb */}
      <div className="mb-2 text-sm text-gray-500">
        <Link to="/azioni-correttive" className="hover:text-primary-600">
          Azioni Correttive
        </Link>
        <span className="mx-2">/</span>
        <span>{azione.nome}</span>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{azione.nome}</h1>
            <Badge variant={statoConf.variant}>{statoConf.label}</Badge>
            <Badge variant={prioConf.variant}>{prioConf.label}</Badge>
          </div>
          {azione.scaduta && (
            <p className="text-red-600 text-sm font-medium mt-1">
              Scadenza superata!
            </p>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          {transizioniVisibili.map((t) => (
            <Button
              key={t.stato}
              variant={t.variant}
              onClick={() => handleCambioStato(t.stato)}
              loading={saving}
              size="sm"
            >
              {t.label}
            </Button>
          ))}
          {isAdmin && (
            <Button
              variant="danger"
              onClick={() => setShowDeleteModal(true)}
              size="sm"
            >
              Elimina
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonna principale */}
        <div className="lg:col-span-2 space-y-6">
          {/* Descrizione azione */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">Azione Richiesta</h2>
            <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 rounded-lg p-4">
              {azione.descrizione}
            </p>
            {azione.note && (
              <div className="mt-4">
                <h3 className="text-sm font-medium text-gray-500 mb-2">Note</h3>
                <p className="text-gray-700 whitespace-pre-wrap">
                  {azione.note}
                </p>
              </div>
            )}
          </Card>

          {/* Esito finale */}
          {(azione.esitoValutazione !== null &&
            azione.esitoValutazione !== undefined) ||
          azione.esito ? (
            <Card>
              <h2 className="text-lg font-semibold mb-4">Esito Finale</h2>
              {azione.esitoValutazioneLabel && (
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-sm text-gray-500">Valutazione:</span>
                  <Badge
                    variant={
                      azione.esitoValutazione === 100000000
                        ? "success"
                        : "danger"
                    }
                  >
                    {azione.esitoValutazioneLabel}
                  </Badge>
                </div>
              )}
              {azione.esito && (
                <div>
                  <span className="text-sm text-gray-500">Note esito:</span>
                  <p className="text-gray-900 whitespace-pre-wrap bg-gray-50 rounded-lg p-3 mt-1">
                    {azione.esito}
                  </p>
                </div>
              )}
            </Card>
          ) : null}

          {/* Timeline Annotazioni */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">
              Attivita e Annotazioni
              {annotazioni.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-400">
                  ({annotazioni.length})
                </span>
              )}
            </h2>

            {annotazioni.length > 0 ? (
              <div className="space-y-4">
                {annotazioni.map((ann) => (
                  <div key={ann.id} className="flex gap-3">
                    {/* Timeline dot */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${
                          ann.tipo === "cambio_stato"
                            ? "bg-blue-500"
                            : ann.tipo === "allegato"
                              ? "bg-purple-500"
                              : "bg-gray-400"
                        }`}
                      />
                      <div className="w-px flex-1 bg-gray-200 mt-1" />
                    </div>

                    {/* Content */}
                    <div className="pb-4 flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-gray-900">
                          {ann.autore}
                        </span>
                        <span className="text-xs text-gray-400">
                          {new Date(ann.data).toLocaleString("it-IT", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                        {ann.tipo === "cambio_stato" && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Cambio stato
                          </span>
                        )}
                        {ann.tipo === "allegato" && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            Allegato
                          </span>
                        )}
                      </div>
                      {ann.tipo === "cambio_stato" &&
                        ann.statoDa !== undefined && (
                          <p className="text-xs text-gray-500 mb-1">
                            {STATO_CONFIG[ann.statoDa]?.label || "?"} &rarr;{" "}
                            <span className="font-medium text-gray-700">
                              {STATO_CONFIG[ann.statoA]?.label || "?"}
                            </span>
                          </p>
                        )}
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {ann.testo}
                      </p>
                      {ann.esitoValutazioneLabel && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">Esito:</span>
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              ann.esitoValutazione === 100000000
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                            }`}
                          >
                            {ann.esitoValutazioneLabel}
                          </span>
                        </div>
                      )}
                      {ann.noteEsito && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                          Note: {ann.noteEsito}
                        </p>
                      )}
                      {ann.allegato && (
                        <a
                          href={ann.allegato.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-1 text-xs text-primary-600 hover:underline"
                        >
                          <svg
                            className="w-3.5 h-3.5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                            />
                          </svg>
                          {ann.allegato.nome}
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">
                Nessuna annotazione
              </p>
            )}

            {/* Form nuova annotazione */}
            {canAnnotateAction && isOpen && (
              <div className="mt-4 pt-4 border-t">
                <Textarea
                  placeholder="Aggiungi un'annotazione..."
                  value={nuovaAnnotazione}
                  onChange={(e) => setNuovaAnnotazione(e.target.value)}
                  rows={2}
                />
                <div className="flex justify-end mt-2">
                  <Button
                    size="sm"
                    onClick={handleAggiungiAnnotazione}
                    loading={savingNota}
                    disabled={!nuovaAnnotazione.trim()}
                  >
                    Aggiungi nota
                  </Button>
                </div>
              </div>
            )}
          </Card>

          {/* Allegati */}
          <Card>
            <h2 className="text-lg font-semibold mb-4">
              Allegati
              {allegati.length > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-400">
                  ({allegati.length})
                </span>
              )}
            </h2>

            {allegati.length > 0 ? (
              <div className="space-y-2">
                {allegati.map((doc) => (
                  <a
                    key={doc.id}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <svg
                      className="w-8 h-8 text-red-500 flex-shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                      />
                    </svg>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{doc.nome}</p>
                      <p className="text-xs text-gray-500">
                        {doc.dataCreazione
                          ? new Date(doc.dataCreazione).toLocaleDateString(
                              "it-IT",
                            )
                          : ""}
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 text-center py-4">
                Nessun allegato
              </p>
            )}

            {canAnnotateAction && isOpen && (
              <div className="mt-3">
                <FileUpload
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  maxSize={100}
                  onChange={handleUploadAllegato}
                  helper="Carica documentazione (PDF, immagini, Office - max 100MB)"
                  disabled={uploading}
                />
              </div>
            )}
          </Card>

          {/* Intervento collegato */}
          {azione.interventoDetail && (
            <Card>
              <h2 className="text-lg font-semibold mb-4">
                Intervento Collegato
              </h2>
              <dl className="grid grid-cols-2 gap-4">
                <div>
                  <dt className="text-sm text-gray-500">Nome</dt>
                  <dd>
                    <Link
                      to={`/interventi/${azione.interventoDetail.id}`}
                      className="text-primary-600 hover:underline font-medium"
                    >
                      {azione.interventoDetail.nome}
                    </Link>
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Data Intervento</dt>
                  <dd className="font-medium">
                    {azione.interventoDetail.dataIntervento
                      ? new Date(
                          azione.interventoDetail.dataIntervento,
                        ).toLocaleDateString("it-IT")
                      : "-"}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm text-gray-500">Stato Validazione</dt>
                  <dd>
                    {azione.interventoDetail.statoValidazione === 100000002 ? (
                      <Badge variant="danger">Azione correttiva</Badge>
                    ) : azione.interventoDetail.validato ? (
                      <Badge variant="success">Validato</Badge>
                    ) : (
                      <Badge variant="warning">Da validare</Badge>
                    )}
                  </dd>
                </div>
              </dl>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Info assegnazione */}
          <Card>
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Assegnazione
            </h3>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Soggetto incaricato</dt>
                <dd className="font-medium text-gray-900">
                  {azione.soggettoAssegnato}
                </dd>
              </div>
              {azione.emailAssegnato && (
                <div>
                  <dt className="text-gray-500">Email</dt>
                  <dd className="font-medium">
                    <a
                      href={`mailto:${azione.emailAssegnato}`}
                      className="text-primary-600 hover:underline"
                    >
                      {azione.emailAssegnato}
                    </a>
                  </dd>
                </div>
              )}
              {azione.rsa && (
                <div>
                  <dt className="text-gray-500">RSA</dt>
                  <dd className="font-medium">{azione.rsa.nome}</dd>
                </div>
              )}
              {azione.fornitore && (
                <div>
                  <dt className="text-gray-500">Fornitore</dt>
                  <dd className="font-medium">{azione.fornitore.nome}</dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Scadenze */}
          <Card>
            <h3 className="text-sm font-medium text-gray-500 mb-3">
              Tempistiche
            </h3>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500">Scadenza</dt>
                <dd
                  className={`font-medium ${azione.scaduta ? "text-red-600" : "text-gray-900"}`}
                >
                  {azione.scadenza
                    ? new Date(azione.scadenza).toLocaleDateString("it-IT")
                    : "-"}
                  {azione.scaduta && " (SCADUTA)"}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Data creazione</dt>
                <dd className="font-medium">
                  {azione.dataCreaazione
                    ? new Date(azione.dataCreaazione).toLocaleDateString(
                        "it-IT",
                      )
                    : "-"}
                </dd>
              </div>
              {azione.dataChiusura && (
                <div>
                  <dt className="text-gray-500">Data chiusura</dt>
                  <dd className="font-medium">
                    {new Date(azione.dataChiusura).toLocaleDateString("it-IT")}
                  </dd>
                </div>
              )}
            </dl>
          </Card>

          {/* Timeline stati */}
          <Card>
            <h3 className="text-sm font-medium text-gray-500 mb-3">Stato</h3>
            <div className="space-y-2">
              {Object.entries(STATO_CONFIG).map(([val, conf]) => {
                const statoNum = parseInt(val);
                const isActive = azione.stato === statoNum;
                const isPast = azione.stato > statoNum;
                const colors = COLORI_STATO[conf.color];
                return (
                  <div key={val} className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{
                        backgroundColor: isActive
                          ? colors.active
                          : isPast
                            ? colors.past
                            : "#e5e7eb",
                      }}
                    />
                    <span
                      className={`text-sm ${isActive ? "font-medium text-gray-900" : isPast ? "text-gray-500" : "text-gray-300"}`}
                    >
                      {conf.label}
                    </span>
                    {isActive && (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full ml-auto">
                        attuale
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      </div>

      {/* Modal cambio stato con annotazione */}
      <Modal
        isOpen={showEsitoModal}
        onClose={() => {
          setShowEsitoModal(false);
          setPendingStato(null);
          setEsitoText("");
          setAnnotazioneStato("");
        }}
        title={modalConf.title}
      >
        <div className="space-y-4">
          <p className="text-gray-600">{modalConf.desc}</p>

          {modalConf.showEsitoValutazione && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Esito valutazione *
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setEsitoValutazione("100000000")}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all text-center font-medium ${
                    esitoValutazione === "100000000"
                      ? "border-green-500 bg-green-50 text-green-700"
                      : "border-gray-200 bg-white text-gray-500 hover:border-green-300 hover:bg-green-50"
                  }`}
                >
                  <span className="block text-lg mb-1">✓</span>
                  Positivo
                </button>
                <button
                  type="button"
                  onClick={() => setEsitoValutazione("100000001")}
                  className={`flex-1 py-3 px-4 rounded-lg border-2 transition-all text-center font-medium ${
                    esitoValutazione === "100000001"
                      ? "border-red-500 bg-red-50 text-red-700"
                      : "border-gray-200 bg-white text-gray-500 hover:border-red-300 hover:bg-red-50"
                  }`}
                >
                  <span className="block text-lg mb-1">✗</span>
                  Negativo
                </button>
              </div>
            </div>
          )}

          <Textarea
            label={
              modalConf.showEsitoValutazione
                ? "Note esito (opzionale)"
                : "Annotazione"
            }
            value={
              modalConf.showEsitoValutazione ? esitoText : annotazioneStato
            }
            onChange={(e) =>
              modalConf.showEsitoValutazione
                ? setEsitoText(e.target.value)
                : setAnnotazioneStato(e.target.value)
            }
            placeholder={
              modalConf.showEsitoValutazione
                ? "Note aggiuntive sull'esito..."
                : "Descrivi cosa hai fatto o cosa e stato rilevato..."
            }
            rows={3}
          />

          {modalConf.showEsitoValutazione && (
            <Textarea
              label="Annotazione aggiuntiva (opzionale)"
              value={annotazioneStato}
              onChange={(e) => setAnnotazioneStato(e.target.value)}
              placeholder="Note operative aggiuntive..."
              rows={2}
            />
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="ghost"
              onClick={() => {
                setShowEsitoModal(false);
                setPendingStato(null);
                setEsitoText("");
                setAnnotazioneStato("");
                setEsitoValutazione("");
              }}
            >
              Annulla
            </Button>
            <Button
              variant="primary"
              onClick={handleConfermaStato}
              loading={saving}
              disabled={modalConf.showEsitoValutazione && !esitoValutazione}
            >
              Conferma
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal Elimina */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Elimina Azione Correttiva"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Sei sicuro di voler eliminare l'azione correttiva "{azione.nome}"?
          </p>
          <p className="text-red-600 text-sm">
            Questa azione non puo essere annullata.
          </p>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              Annulla
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={saving}>
              Elimina
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default DettaglioAzioneCorrettivaPage;
